import React from 'react';
import { gql, useQuery } from '@apollo/client';
import {
  Layout,
  normalize,
  routes,
  GetRandomDefaultTitle,
  pageTypes,
} from 'gatsby-theme-apollo';
import Footer from '../templates/page/footer';
import GameContent from '../templates/page/content';
import TierListEditor from '../templates/tier-lists/tier-list-editor';
import { defaultTiers } from '../templates/tier-lists/tier';

const GET_TIERLIST = gql`
  query GetTierList($tierListGameName: String!, $tierListPermalink: String!) { 
    games (where: {name_contains: $tierListGameName}){  
      name  
      id  
      EntityConfigs 
      { 
        singular  
        plural  
        primary 
      } 
    } 
    tierlists (limit: 1   
      where : {   
      permalink_contains: $tierListPermalink  
  }) {  
      id  
      tierJSON  
      title 
      description 
      permalink 
      richDescription 
      lastPublish 
      comments {  
        text  
        createdAt 
        author {  
          id  
          name  
        } 
      } 
      author {  
        name  
        id  
      }   
    } 
  } 
`;

const TierListEditorPage = ({ location, pageContext }) => {
  const {
    author,
    title,
    description,
    richDescription,
    tierJSON,
    id,
    stats,
    comments,
    lastPublish
  } = pageContext;

  // These aren't const as they may be computed and modified for unbaked tier lists
  let { gameID, gameName, entities, primaryEntityTerm, EntityConfigs } = pageContext;

  // If we have no title or gameName, or just a title, this is a published tier list.
  const isPublished = (!gameName && !title) || title;

  const pageType = pageTypes.TIER_LIST_EDITOR;
  const pageHeader = `${gameName} Tier List Maker`;

  // If we have a game prop, this page is baked
  const isBaked = false;

  const primaryEntityConfig = EntityConfigs && EntityConfigs.find(term => term.primary);

  let tierListTitle = title;
  let tierListAuthor = author;
  let tierListID = id;
  let tierListDescription = description || '';
  let tierListRichDescription = richDescription;
  let tierData = (tierJSON && tierJSON.dimensions[0].tiers) || defaultTiers();
  let primaryEntitySet = entities;
  let tierListLastPublish = lastPublish;

  let getDataFromPath = path => {
    // enforce trailing slash since we rely on it for offsets
    if (path.substring(path.length-1) !== "/") path = path + '/';
    const pathSegments = path.split('/');
    let pathGameName = pathSegments[pathSegments.length - 5];
    pathGameName = normalize.unhyphenateAndLowerCase(pathGameName);
    const pathTierListPermalink = pathSegments[pathSegments.length - 3];
    const isExistingTierlist = pathSegments[pathSegments.length - 2] === 'edit';
    return {
      isExistingTierlist,
      pathGameName,
      pathTierListPermalink
    };
  };

  const { isExistingTierlist, pathGameName, pathTierListPermalink } = getDataFromPath(location.pathname);

  const crumbs = [
    { text: `${pathGameName} Wiki`, url: routes.GameHub(gameName), hide: true },
    { text: `${pathGameName} Tier Lists`, url: routes.TierListHub(gameName) }
  ];

  const TierListQueryResult = () => {
    const { loading, error, data } = useQuery(GET_TIERLIST, {
      fetchPolicy: 'no-cache',
      variables: { tierListGameName: pathGameName, tierListPermalink: pathTierListPermalink },
    });

    if (error) {
      console.error(error);
      return <p>Error :(</p>;
    }

    let tierListComments;

    // Todo: When we enable multidimensional editing, use a "default" dimension rather than the first
    if (!loading) {
      data.tierlists.forEach(tierlist => {
        tierData = tierlist.tierJSON.dimensions[0].tiers;
        tierListDescription = tierlist.description;
        tierListTitle = tierlist.title;
        tierListRichDescription = tierlist.richDescription;
        tierListAuthor = tierlist.author;
        tierListID = tierlist.id;
        tierListLastPublish = tierlist.lastPublish;
        tierListComments = tierlist.comments;
      });

      if (!tierListTitle) {
        tierListTitle = GetRandomDefaultTitle({
          entityTerm: (primaryEntityConfig && primaryEntityConfig.plural) || 'Characters'
        })
      }
    }

    return (
      <>
       <TierListEditor  
          characters={primaryEntitySet} 
          gameStats={stats} 
          gameName={gameName} 
          gameID={gameID} 
          entityTypePlural={primaryEntityTerm && primaryEntityTerm.plural}  
          tierData={tierData} 
          tierListTitle={tierListTitle} 
          tierListAuthor={tierListAuthor} 
          tierListID={tierListID} 
          tierListDescription={tierListDescription} 
          tierListRichDescription={tierListRichDescription} 
          tierListLastPublish={tierListLastPublish} 
          isPublished={isPublished} 
          loading={loading}
          comments={tierListComments || comments || []} 
          isBaked={false}
        />
      </>
    );
  };

  return (
    <Layout
      location={location}
      gameName={pathGameName}
      hideHeaderLogo={!isBaked}
      hideBottomNav={true}
      enableCollapsedSidebar
      affixHeaderTop
      pageType={pageType}
      title={isPublished ? tierListTitle : pageHeader}
    >
      <GameContent header={pageHeader} noHeader={false} crumbs={crumbs}>
        {!isExistingTierlist ? (  
          <TierListEditor 
            characters={primaryEntitySet} 
            gameStats={stats} 
            gameName={gameName} 
            gameID={gameID} 
            entityTypePlural={primaryEntityTerm && primaryEntityTerm.plural}  
            tierData={tierData} 
            tierListTitle={tierListTitle} 
            tierListAuthor={tierListAuthor} 
            tierListID={tierListID} 
            tierListDescription={tierListDescription} 
            tierListRichDescription={tierListRichDescription} 
            tierListLastPublish={tierListLastPublish} 
            isPublished={isPublished} 
            comments={comments || []} 
            isBaked 
          />  
        ) : (
          <TierListQueryResult />
        )}
        <Footer />
      </GameContent>
    </Layout>
  );
};
export default TierListEditorPage;
